import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, Input, Label, Table, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";

const URL_PUERTOS = process.env.REACT_APP_URL_PUERTOS;
const auth = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const VACIO = { name: "", tipo: "Origen", codigo: "", pais: "", alias: "" };

// Catálogo de puertos (origen y destino). Los alias son las variantes con que se capturaba en texto libre
// (NGB, LAZARO, LONGBEACH…): sirven para reconocerlos y normalizar.
export default function ListadoPuertos() {
  const { user } = useContext(AuthContext);
  const [t] = useTranslation("global");
  const [puertos, setPuertos] = useState([]);
  const [tipo, setTipo] = useState("");
  const [busca, setBusca] = useState("");
  const [form, setForm] = useState(null); // VACIO o puerto en edición

  const carga = () =>
    axios
      .get(URL_PUERTOS, { headers: auth() })
      .then(({ data }) => setPuertos(Array.isArray(data) ? data : []))
      .catch(() => setPuertos([]));
  useEffect(() => { carga(); }, []);

  const visibles = useMemo(() => {
    const q = busca.trim().toUpperCase();
    return puertos
      .filter((p) => !tipo || p.tipo === tipo)
      .filter((p) => !q || [p.name, p.codigo, p.pais, ...(p.alias || [])].some((v) => String(v || "").toUpperCase().includes(q)));
  }, [puertos, tipo, busca]);

  const guardar = async () => {
    const body = {
      name: form.name,
      tipo: form.tipo,
      codigo: form.codigo || "",
      pais: form.pais || "",
      alias: String(form.alias || "").split(",").map((x) => x.trim()).filter(Boolean),
    };
    if (!body.name.trim()) return Swal.fire({ icon: "warning", title: t("Captura el nombre del puerto") });
    try {
      if (form._id) await axios.patch(`${URL_PUERTOS}/${form._id}`, body, { headers: auth() });
      else await axios.post(URL_PUERTOS, body, { headers: auth() });
      setForm(null);
      carga();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: t("Guardado"), showConfirmButton: false, timer: 1400 });
    } catch (err) {
      Swal.fire({ icon: "error", title: t("No se pudo guardar"), text: err?.response?.data?.message || err.message });
    }
  };

  const activa = async (p) => {
    const is_active = p.is_active === "Si" ? "No" : "Si";
    await axios.patch(`${URL_PUERTOS}/${p._id}`, { is_active }, { headers: auth() });
    setPuertos((ps) => ps.map((x) => (x._id === p._id ? { ...x, is_active } : x)));
  };

  if (!user.menuNavieras) return <Header />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <Header />
      <div className="container col-sm-11">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-anchor" /></span>
              {t("Puertos")}
            </h3>
            <p className="page-subtitle">{t("Puertos de origen y destino que se eligen al capturar un contenedor")}</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="success" onClick={() => setForm({ ...VACIO })}><i className="fas fa-plus" /> {t("Nuevo")}</Button>
          </div>
        </div>
        <div className="stats-row">
          <div className="stat-chip"><i className="fas fa-sign-out-alt" style={{ color: "#2563eb" }} /> {t("Origen")} <span>{puertos.filter((p) => p.tipo === "Origen").length}</span></div>
          <div className="stat-chip"><i className="fas fa-sign-in-alt" style={{ color: "#16a34a" }} /> {t("Destino")} <span>{puertos.filter((p) => p.tipo === "Destino").length}</span></div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="filter-bar">
              <div>
                <Label>{t("Tipo")}</Label>
                <Input bsSize="sm" type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="">{t("Todos")}</option>
                  <option value="Origen">{t("Origen")}</option>
                  <option value="Destino">{t("Destino")}</option>
                </Input>
              </div>
              <div>
                <Label>{t("Buscar")}</Label>
                <Input bsSize="sm" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="NGB, LAZARO…" />
              </div>
            </div>
            <Table size="sm" hover className="table-responsive-xl">
              <thead>
                <tr>
                  <th>{t("Puerto")}</th><th>{t("Tipo")}</th><th>{t("Código")}</th><th>{t("País")}</th>
                  <th>{t("También se capturaba como")}</th><th>{t("Activo")}</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((p) => (
                  <tr key={p._id} className={p.is_active === "No" ? "td-muted" : undefined}>
                    <td className="td-strong">{p.name}</td>
                    <td><span className={`badge-soft badge-soft-${p.tipo === "Origen" ? "info" : "success"}`}>{p.tipo}</span></td>
                    <td>{p.codigo || "—"}</td>
                    <td>{p.pais || "—"}</td>
                    <td className="td-muted" style={{ maxWidth: 360 }}>{(p.alias || []).join(", ") || "—"}</td>
                    <td>
                      <button type="button" className={`badge-soft badge-soft-${p.is_active === "No" ? "secondary" : "success"} rv-badge-btn`} onClick={() => activa(p)}>
                        {p.is_active === "No" ? "No" : t("Si")}
                      </button>
                    </td>
                    <td>
                      <Button size="sm" color="info" onClick={() => setForm({ ...p, alias: (p.alias || []).join(", ") })}>
                        <i className="far fa-edit" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {visibles.length === 0 ? <div className="td-muted">{t("Sin puertos")}</div> : null}
          </div>
        </div>
      </div>

      <Modal isOpen={!!form} toggle={() => setForm(null)}>
        <ModalHeader toggle={() => setForm(null)}>{form && form._id ? t("Editar") : t("Nuevo")} {t("puerto")}</ModalHeader>
        <ModalBody>
          {form ? (
            <Row>
              <Col md={8} className="mb-2"><Label>{t("Nombre")}</Label><Input value={form.name} onChange={set("name")} /></Col>
              <Col md={4} className="mb-2">
                <Label>{t("Tipo")}</Label>
                <Input type="select" value={form.tipo} onChange={set("tipo")}>
                  <option value="Origen">{t("Origen")}</option>
                  <option value="Destino">{t("Destino")}</option>
                </Input>
              </Col>
              <Col md={4} className="mb-2"><Label>{t("Código")}</Label><Input value={form.codigo} onChange={set("codigo")} placeholder="CNNGB" /></Col>
              <Col md={8} className="mb-2"><Label>{t("País")}</Label><Input value={form.pais} onChange={set("pais")} /></Col>
              <Col md={12}>
                <Label>{t("También se capturaba como (separado por comas)")}</Label>
                <Input value={form.alias} onChange={set("alias")} placeholder="NGB, Ningbo" />
              </Col>
            </Row>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={guardar}><i className="fas fa-save" /> {t("Guardar")}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
